import { useContext, useState, useCallback } from 'react';
import { apiPost } from './apiPost';
import { AppContext } from 'contexts/AppContext';

/**
 * Data access for the Quality tab.
 *
 * /send/quality-metrics       → the "Response quality" card
 * /send/unhelpful-responses   → the "Null / unhelpful responses" card
 *
 * A metric returned as `null` is null by MEASUREMENT, not by omission — the
 * event that would produce it does not exist. Follow-up rate needs an event
 * distinguishing a follow-up from a fresh question; per-tool latency needs
 * timings the writer never records; a true abandon rate needs a widget-open
 * event, and its closest source has been dead since 2025-06-04.
 *
 * Nothing here coerces null to 0. A zero on this tab would read as "measured,
 * and it is nothing", which is a stronger claim than the data supports.
 */

// 10, not 25. At 25 the two list cards were each taller than a viewport, which
// pushed everything below them off-screen and left the short stats card sitting
// beside a wall of whitespace. Pagination already exists -- the page just has to
// be a page.
const PAGE_SIZE = 10;

export const useQualityData = () => {
  const { dateRange, selectedClient, getAccessTokenSilently, base_url, platformFilter, environmentFilter, languageFilter } = useContext(AppContext);

  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);

  const [categories, setCategories] = useState(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [tools, setTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);

  const [written, setWritten] = useState([]);
  const [writtenTotal, setWrittenTotal] = useState(0);
  const [writtenLoading, setWrittenLoading] = useState(false);
  const [writtenPage, setWrittenPage] = useState(0);

  const [unhelpful, setUnhelpful] = useState([]);
  const [unhelpfulTotal, setUnhelpfulTotal] = useState(0);
  const [unhelpfulLoading, setUnhelpfulLoading] = useState(false);
  const [unhelpfulError, setUnhelpfulError] = useState(null);
  const [page, setPage] = useState(0);

  // Tolerates a failed silent token fetch -- see hooks/apiPost.js. Without it
  // Auth0 throws `consent_required` before the request is ever made, so the tab
  // cannot be exercised on localhost at all.
  const post = useCallback(
    (path, body) => apiPost(base_url, getAccessTokenSilently, path, body),
    [base_url, getAccessTokenSilently],
  );

  // dateRange is an ARRAY [from, to] of Dates, not {from,to} — AppContext.js:152.
  const range = useCallback(() => {
    const [from, to] = Array.isArray(dateRange) ? dateRange : [];
    if (!from || !to || !selectedClient?.key) return null;
    return {
      start_date: new Date(from).toISOString().split('T')[0],
      end_date: new Date(to).toISOString().split('T')[0],
      client: selectedClient.key,
      // Sent from one place so no individual fetch can forget it. Endpoints
      // that cannot honour it ignore it and report so in their response.
      ...(platformFilter ? { platform: platformFilter } : {}),
      // Always explicit -- 'prod' | 'stage' | 'all' -- so what the buttons show
      // is exactly what the query ran with. Absent would also mean 'prod' (the
      // backend default), but an implicit default is how filters get dropped.
      environment: environmentFilter || 'all',
      ...(languageFilter ? { language: languageFilter } : {}),
    };
  }, [dateRange, selectedClient, platformFilter, environmentFilter, languageFilter]);

  const fetchMetrics = useCallback(async ({ platform, excludeInternal, internalOnly } = {}) => {
    const base = range();
    if (!base) return;
    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const body = { ...base };
      if (platform) body.platform = platform;
      if (excludeInternal) body.exclude_internal = true;
      if (internalOnly) body.internal_only = true;
      const data = await post('quality-metrics', body);
      setMetrics(data?.data || null);
    } catch (err) {
      setMetricsError(err.response?.data?.detail || err.message || 'Failed to load quality metrics');
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [range, post]);

  const fetchUnhelpful = useCallback(async ({ classification, pageIndex = 0, excludeInternal, internalOnly } = {}) => {
    const base = range();
    if (!base) return;
    setUnhelpfulLoading(true);
    setUnhelpfulError(null);
    try {
      const body = { ...base, limit: PAGE_SIZE, offset: pageIndex * PAGE_SIZE };
      if (classification) body.classification = classification;
      if (excludeInternal) body.exclude_internal = true;
      if (internalOnly) body.internal_only = true;
      const data = await post('unhelpful-responses', body);
      setUnhelpful(data?.data || []);
      setUnhelpfulTotal(data?.total ?? 0);
      setPage(pageIndex);
    } catch (err) {
      setUnhelpfulError(err.response?.data?.detail || err.message || 'Failed to load responses');
      setUnhelpful([]);
      setUnhelpfulTotal(0);
    } finally {
      setUnhelpfulLoading(false);
    }
  }, [range, post]);

  // Per-tool latency and success, from metadata.function_calls. This needs no
  // instrumentation -- the durations were already stored.
  const fetchTools = useCallback(async ({ platform, excludeInternal, internalOnly } = {}) => {
    const base = range();
    if (!base) return;
    setToolsLoading(true);
    try {
      const body = { ...base };
      if (platform) body.platform = platform;
      if (excludeInternal) body.exclude_internal = true;
      if (internalOnly) body.internal_only = true;
      const data = await post('tool-performance', body);
      setTools(data?.data || []);
    } catch {
      setTools([]);
    } finally {
      setToolsLoading(false);
    }
  }, [range, post]);

  // What users typed about why an answer was wrong. Stronger signal than the
  // wording heuristic, because it is the user's own account.
  const fetchWritten = useCallback(async ({ pageIndex = 0, excludeInternal, internalOnly } = {}) => {
    const base = range();
    if (!base) return;
    setWrittenLoading(true);
    try {
      const data = await post('written-feedback',
        { ...base, limit: PAGE_SIZE, offset: pageIndex * PAGE_SIZE,
          ...(excludeInternal ? { exclude_internal: true } : {}),
          ...(internalOnly ? { internal_only: true } : {}) });
      setWritten(data?.data || []);
      setWrittenTotal(data?.total ?? 0);
      setWrittenPage(pageIndex);
    } catch {
      setWritten([]);
      setWrittenTotal(0);
    } finally {
      setWrittenLoading(false);
    }
  }, [range, post]);

  // Stored classification, not an inference -- see QueryCategoriesCard.
  const fetchCategories = useCallback(async ({ excludeInternal, internalOnly } = {}) => {
    const base = range();
    if (!base) return;
    setCategoriesLoading(true);
    try {
      const data = await post('query-categories', {
        ...base,
        ...(excludeInternal ? { exclude_internal: true } : {}),
        ...(internalOnly ? { internal_only: true } : {}),
      });
      setCategories(data?.data || null);
    } catch {
      setCategories(null);
    } finally {
      setCategoriesLoading(false);
    }
  }, [range, post]);

  return {
    categories, categoriesLoading, fetchCategories,
    tools, toolsLoading, fetchTools,
    written, writtenTotal, writtenLoading, writtenPage, fetchWritten,
    metrics, metricsLoading, metricsError, fetchMetrics,
    unhelpful, unhelpfulTotal, unhelpfulLoading, unhelpfulError,
    page, pageSize: PAGE_SIZE, fetchUnhelpful,
  };
};

export default useQualityData;
